import {
  DayLabel,
  MonthLabel,
  ProductServiceDiscriminator,
  ServiceDiscriminator,
  ServiceName,
  SuppliedProduct,
} from 'types/common'
import {
  EventProductStatus,
  EventStatus,
  Genders,
  PaymentMethod,
  PaymentStatus,
  ProfessionalSpecialType,
  QuestionType,
  ProfessionalOutfit,
  WeekDay,
  VirtualExpSpecialType,
  LocationFeatureCategory,
  LocationArrangementName,
  LocationArrangementStatus,
  FacetFilterGroup,
  TransactionStatus,
  ActivityStatus,
} from 'types/form'

import {
  PricingRuleMode,
  RulePricingType,
} from 'services/api/entities/catalog/types'
import {
  FacetCategory,
  PricingFacetType,
} from 'services/api/entities/catalog/facets'
import { KampaayerRole } from 'services/api/entities/crm/kampaayers'
import { DeliveryTimeLogic } from 'services/api/entities/catalog/packages/foodPackage'
import {
  RuleAction,
  RuleOperator,
  RuleTargetType,
  RuleWhat,
} from 'services/api/entities/Rules'
import { DrinkSpecialType } from 'services/api/entities/catalog/sections/drinkSections'
import { MembershipRole } from 'services/api/entities/crm/organizations/types/internal/organization-model'

export type TypedChoices<T> = { id: T; name: string }[]

export const DEFAULT_PRICE = 1

export const DEFAULT_PRICE_RANGE_MAX_QUANTITY = 10000

export const STANDARD_PRICING_STARTUP = [
  {
    variableCost: 0,
    fixedCost: 0,
    fixedPrice: 0,
    variablePrice: 0,
    maxQuantity: 10000,
  },
]

export const kampaayServicesList: Exclude<ServiceName, 'logistic'>[] = [
  'food',
  'drink',
  'rent',
  'music',
  'virtual',
]

export const serviceDiscriminatorArray: ServiceDiscriminator[] = [
  'DrinkConfigurator',
  'FoodConfigurator',
  'GeneralConfigurator',
  'MusicConfigurator',
  'RentConfigurator',
  'VirtualConfigurator',
]

export const ARRANGEMENT_NAMES_ARRAY: LocationArrangementName[] = [
  'classroom',
  'theater',
  'cabaret',
  'boardroom',
  'ushape',
  'standing',
]

export const weekDaysList: TypedChoices<WeekDay> = [
  { id: 'sunday', name: 'Sunday' },
  { id: 'monday', name: 'Monday' },
  { id: 'tuesday', name: 'Tuesday' },
  { id: 'wednesday', name: 'Wednesday' },
  { id: 'thursday', name: 'Thursday' },
  { id: 'friday', name: 'Friday' },
  { id: 'saturday', name: 'Saturday' },
]

export const activityStatusChoices: TypedChoices<ActivityStatus> = [
  { id: 'active', name: 'Active' },
  { id: 'inactive', name: 'Inactive' },
]

export const invoiceStatusList = [
  { id: 'missingData', name: 'Missing Data' },
  { id: 'ready', name: 'Ready' },
  { id: 'invoiced', name: 'Invoiced' },
  { id: 'notToInvoice', name: 'Not to Invoice' },
]

export const accountingStatusList = [
  { id: 'accounted', name: 'Accounted' },
  { id: 'deleted', name: 'Deleted' },
]

export const paymentStatusList: TypedChoices<PaymentStatus> = [
  { id: 'none', name: 'None' },
  { id: 'payed', name: 'Payed' },
  { id: 'expired', name: 'Expired' },
]

export const paymentMethodChoices: TypedChoices<PaymentMethod> = [
  { id: 'card', name: 'Card' },
  { id: 'bankTransfer', name: 'Bank Transfer' },
  { id: 'none', name: 'None' },
]

export const productStatusChoices: TypedChoices<EventProductStatus> = [
  { id: 'created', name: 'Created' },
  { id: 'delivered', name: 'Delivered' },
  { id: 'cancelled', name: 'Cancelled' },
]

export const eventStatusChoices: TypedChoices<EventStatus> = [
  { id: 'confirmed', name: 'Confirmed' },
  { id: 'closed', name: 'Closed' },
  { id: 'invoiced', name: 'Invoiced' },
  { id: 'draft', name: 'Draft' },
]

export const transactionStatusChoices: TypedChoices<TransactionStatus> = [
  { id: 'confirmed', name: 'Confirmed' },
  { id: 'draft', name: 'Draft' },
]

export const professionalOutfitChoices: TypedChoices<ProfessionalOutfit> = [
  { id: 'blackShirt', name: 'Black Shirt' },
  { id: 'blackShirtApron', name: 'Black Shirt with apron' },
  { id: 'whiteShirt', name: 'White Shirt' },
  { id: 'whiteShirtApron', name: 'White Shirt with apron' },
]

export const genderChoice: TypedChoices<Genders> = [
  { id: 'male', name: 'Male' },
  { id: 'female', name: 'Female' },
]

export const supplierCategoryChoices: TypedChoices<SuppliedProduct> = [
  { id: 'food', name: 'Food' },
  { id: 'music', name: 'Music' },
  { id: 'rent', name: 'Rent' },
  { id: 'professional', name: 'Professionals' },
  { id: 'physical', name: 'Physical Experiences' },
  { id: 'location', name: 'Location' },
]

export const briefQuestionChoices: TypedChoices<QuestionType> = [
  { id: 'boolean', name: 'Boolean' },
  { id: 'freeText', name: 'Text' },
  { id: 'multiple', name: 'Multiple' },
  { id: 'date', name: 'Date' },
  { id: 'time', name: 'Time' },
]

export const allowedServicesChoices: TypedChoices<ProductServiceDiscriminator> =
  [
    { id: 'FoodConfigurator', name: 'Foods' },
    { id: 'RentConfigurator', name: 'Rents' },
    { id: 'MusicConfigurator', name: 'Musics' },
    { id: 'DrinkConfigurator', name: 'Drinks' },
    { id: 'VirtualConfigurator', name: 'Virtuals' },
    { id: 'PhysicalConfigurator', name: 'Physicals' },
    { id: 'LocationConfigurator', name: 'Locations' },
  ]

export const professionalSpecialTypeChoices: TypedChoices<ProfessionalSpecialType> =
  [
    { id: 'waitingStaff', name: 'Waiting Staff' },
    { id: 'cleaner', name: 'Cleaner' },
  ]

export const facetDescriptionOptions: TypedChoices<
  Exclude<FacetCategory, 'filterTag'>
> = [
  { id: 'tag', name: 'tag' },
  { id: 'productFilter', name: 'productFilter' },
  { id: 'group', name: 'group' },
  { id: 'pricing', name: 'pricing' },
]

export const pricingFacetChoices: TypedChoices<PricingFacetType> = [
  { id: 'perItem', name: 'perItem' },
  { id: 'perPerson', name: 'perPerson' },
]

export const roles: TypedChoices<MembershipRole> = [
  { id: 'admin', name: 'Admin' },
  { id: 'collaborator', name: 'Collaborator' },
]

export const virtualExpChoices: TypedChoices<VirtualExpSpecialType> = [
  { id: 'virtualCatering', name: 'Virtual Catering' },
]

export const kampaayerRolesChoices: TypedChoices<KampaayerRole> = [
  { id: 'admin', name: 'Admin' },
  { id: 'sales', name: 'Sales' },
  { id: 'ops', name: 'Ops' },
  { id: 'marketing', name: 'Marketing' },
]

export const locationArrangementStatusChoices: TypedChoices<LocationArrangementStatus> =
  [
    { id: 'active', name: 'Active' },
    { id: 'inactive', name: 'Inactive' },
    { id: 'hidden', name: 'Hidden' },
  ]

export const locationFeatureCategoryChoices: TypedChoices<LocationFeatureCategory> =
  [
    { id: 'characteristic', name: 'Characteristic' },
    { id: 'equipment', name: 'Equipment' },
  ]

export const facetFilterGroupChoices: TypedChoices<FacetFilterGroup> = [
  { id: 'Type', name: 'Type' },
  { id: 'Location', name: 'Location' },
  { id: 'EventType', name: 'Event type' },
  { id: 'Setting', name: 'Setting' },
  { id: 'Skill', name: 'Skill' },
  { id: 'TeamBuildingCategory', name: 'Team building category' },
  { id: 'VirtualCategory', name: 'Virtual Category' },
  { id: 'Language', name: 'Language' },
]

export const deliveryTimeLogicChoices: TypedChoices<DeliveryTimeLogic> = [
  { id: 'packageStartTime', name: 'Package start time' },
]

export const EmptyFormExt: React.FC = () => null

const getNameById =
  (collection: { id: string; name: string }[]) => (id: string) =>
    collection.find((i) => i.id === id)?.name

export const getOutfitNameById = getNameById(professionalOutfitChoices)
export const getEventStatusById = getNameById(eventStatusChoices)
export const getProductStatusById = getNameById(eventStatusChoices)

// RULES
export const ruleWhatChoices: TypedChoices<RuleWhat> = [
  { id: 'cart', name: 'Cart' },
  { id: 'selecting', name: 'Selecting' },
]

export const ruleActionChoices: TypedChoices<RuleAction> = [
  { id: 'suggest', name: 'Suggest' },
]

export const ruleOperatorChoices: TypedChoices<RuleOperator> = [
  { id: 'and', name: 'And' },
]

export const ruleTargetTypeChoices: TypedChoices<RuleTargetType> = [
  { id: 'product', name: 'Product' },
  { id: 'package', name: 'Package' },
]

export const serviceDiscriminatorChoices: TypedChoices<ProductServiceDiscriminator> =
  [
    { id: 'MusicConfigurator', name: 'Music' },
    { id: 'RentConfigurator', name: 'Rent' },
    { id: 'FoodConfigurator', name: 'Food' },
    { id: 'DrinkConfigurator', name: 'Drink' },
    { id: 'VirtualConfigurator', name: 'Virtual' },
    { id: 'PhysicalConfigurator', name: 'Physical' },
    { id: 'LocationConfigurator', name: 'Location' },
    { id: 'ProfessionalConfigurator', name: 'Professional' },
  ]

export const drinkSpecialTypeChoices: TypedChoices<DrinkSpecialType> = [
  { id: 'alcoholicCocktail', name: 'Alcoholic Cocktail' },
  { id: 'analcoholicCocktail', name: 'Analcoholic Cocktail' },
  { id: 'beer', name: 'Beer' },
  { id: 'wine', name: 'Wine' },
  { id: 'softDrink', name: 'Soft Drink' },
]

export const monthChoices: TypedChoices<MonthLabel> = [
  { id: '01', name: 'Jan' },
  { id: '02', name: 'Feb' },
  { id: '03', name: 'Mar' },
  { id: '04', name: 'Apr' },
  { id: '05', name: 'May' },
  { id: '06', name: 'Jun' },
  { id: '07', name: 'Jul' },
  { id: '08', name: 'Aug' },
  { id: '09', name: 'Sep' },
  { id: '10', name: 'Oct' },
  { id: '11', name: 'Nov' },
  { id: '12', name: 'Dec' },
]

export const monthDayChoices: DayLabel[] = [
  '01',
  '02',
  '03',
  '04',
  '05',
  '06',
  '07',
  '08',
  '09',
  '10',
  '11',
  '12',
  '13',
  '14',
  '15',
  '16',
  '17',
  '18',
  '19',
  '20',
  '21',
  '22',
  '23',
  '24',
  '25',
  '26',
  '27',
  '28',
  '29',
  '30',
  '31',
]

export const pricingRulesModeChoices: TypedChoices<PricingRuleMode> = [
  { id: 'tpr', name: 'Time Period' },
  { id: 'nhr', name: 'Min Hours' },
]

export const pricingRulesTypeChoices: TypedChoices<RulePricingType> = [
  { id: 'fixed', name: 'Fixed' },
  { id: 'proportional', name: 'Proportional (by hour)' },
]

export const locationPricingNameChoices: TypedChoices<string | undefined> = [
  { id: undefined, name: 'None' },
  { id: 'perHour', name: 'Per hour' },
  { id: 'halfDay', name: 'Half day' },
  { id: 'fullDay', name: 'Full day' },
]
